import { StatusListingColors } from '@labrav/react-components'
import { productWithoutResale } from '../../common/utils/constants'
import {
  MicrosoftOfferStages,
  MicrosoftOfferStageToStatusRelationMapEnum,
  OfferStatus,
} from '../modules/privateOffer/reducer'
import { Product } from '../modules/productsListing/reducer'
import { StatusItem } from '@labrav/react-components/lib/@types/v2/components/StatusListing'
import { isEmpty } from 'lodash'
import {
  faCheck,
  faClipboardCheck,
  faEye,
  faHandshake,
  faPaperPlaneTop,
  faUserLock,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons'

export const isResaleProduct = (product: Product) => {
  return (
    product.productType && !productWithoutResale.includes(product.productType)
  )
}

export const findProductByProductRef = (
  products: Product[],
  productRef?: string
) => {
  if (!productRef) {
    return undefined
  }

  return products.find(
    product =>
      product.productId === productRef ||
      product.cloudMarketplaceProductId === productRef
  )
}

export const getMicrosoftPOStagesData = (
  auditJson: MicrosoftOfferStages,
  agreementStatus?: OfferStatus,
  failedDate?: string | null,
  options?: { hidePendingAccountConfiguration?: boolean }
) => {
  const hidePendingAccountConfiguration =
    options?.hidePendingAccountConfiguration ?? false

  const statges: StatusItem[] = Object.entries(auditJson).map(
    ([status, completed]) => {
      let icon
      let display = true
      let sortOrder = 0
      let completedDate = completed
      let tooltipText = ''

      let color = isEmpty(completed)
        ? StatusListingColors.GREY
        : StatusListingColors.WHITE
      let background = isEmpty(completed)
        ? StatusListingColors.WHITE
        : StatusListingColors.COMPLETED
      let borderColor = isEmpty(completed)
        ? StatusListingColors.BORDER
        : StatusListingColors.COMPLETED
      const isNextCompleted = getNextCompleted(
        status,
        auditJson,
        failedDate,
        hidePendingAccountConfiguration
      )

      switch (status) {
        case MicrosoftOfferStageToStatusRelationMapEnum.offerCreationInProgress:
          icon = faCheck
          color = StatusListingColors.WHITE
          background = StatusListingColors.COMPLETED
          borderColor = StatusListingColors.COMPLETED
          sortOrder = 1
          break
        case MicrosoftOfferStageToStatusRelationMapEnum.offerEmailSent:
          icon =
            isEmpty(completed) &&
            agreementStatus === OfferStatus.OFFER_CREATION_FAILED
              ? faXmark
              : isEmpty(completed)
                ? faPaperPlaneTop
                : faCheck

          background =
            isEmpty(completed) &&
            agreementStatus === OfferStatus.OFFER_CREATION_FAILED
              ? StatusListingColors.REJECTED
              : isEmpty(completed)
                ? StatusListingColors.WHITE
                : StatusListingColors.COMPLETED
          color =
            isEmpty(completed) &&
            agreementStatus === OfferStatus.OFFER_CREATION_FAILED
              ? StatusListingColors.WHITE
              : isEmpty(completed)
                ? StatusListingColors.GREY
                : StatusListingColors.WHITE
          borderColor =
            isEmpty(completed) &&
            agreementStatus === OfferStatus.OFFER_CREATION_FAILED
              ? StatusListingColors.REJECTED
              : isEmpty(completed)
                ? StatusListingColors.BORDER
                : StatusListingColors.COMPLETED
          completedDate =
            isEmpty(completed) &&
            agreementStatus === OfferStatus.OFFER_CREATION_FAILED
              ? failedDate
              : completed
          sortOrder = 2
          break
        case MicrosoftOfferStageToStatusRelationMapEnum.offerViewed:
          icon = isEmpty(completed) ? faEye : faCheck
          sortOrder = 3
          break
        case MicrosoftOfferStageToStatusRelationMapEnum.offerTermsAcceptedBuyer:
          icon = isEmpty(completed) ? faHandshake : faCheck
          sortOrder = 4
          tooltipText = 'Buyer is yet to create the Microsoft subscription.'
          break
        case MicrosoftOfferStageToStatusRelationMapEnum.offerPurchaseComplete:
          icon = isEmpty(completed) ? faClipboardCheck : faCheck
          sortOrder = 5
          tooltipText = 'Buyer is yet to complete their account configuration.'
          if (hidePendingAccountConfiguration) {
            display = false
          }
          break
        case MicrosoftOfferStageToStatusRelationMapEnum.offerAccepted:
          icon = isEmpty(completed) ? faUserLock : faCheck
          sortOrder = 6
          tooltipText = 'Buyer has completed all steps to accept the offer.'
          break
        default:
          icon = faClipboardCheck
          display = false
      }

      return {
        status,
        completed: completedDate,
        icon,
        color,
        background,
        display,
        borderColor,
        sortOrder,
        isNextCompleted,
        tooltipText,
      }
    }
  )

  if (!auditJson.Deactivated) {
    return statges
  }

  const competedStages = statges
    ?.filter(obj => obj.display === true && obj.completed)
    ?.sort((a, b) => (a?.sortOrder || 0) - (b?.sortOrder || 0))

  competedStages[competedStages?.length - 1] = {
    ...competedStages[competedStages?.length - 1],
    verticalLineColor: StatusListingColors.REJECTED,
    isNextCompleted: true,
  }
  return [...competedStages].concat([
    {
      status: MicrosoftOfferStageToStatusRelationMapEnum.offerRestricted,
      completed: auditJson.Deactivated,
      icon: faXmark,
      color: StatusListingColors.WHITE,
      background: StatusListingColors.REJECTED,
      display: true,
      borderColor: StatusListingColors.REJECTED,
      sortOrder: competedStages?.length + 1,
      isNextCompleted: true,
    },
  ])
}

const getNextCompleted = (
  keyName: string | null,
  auditJson: MicrosoftOfferStages,
  failedDate?: string | null,
  hidePendingAccountConfiguration = false
) => {
  switch (keyName) {
    case MicrosoftOfferStageToStatusRelationMapEnum.offerCreationInProgress:
      return !!auditJson?.['Sent to buyer'] || !!failedDate
    case MicrosoftOfferStageToStatusRelationMapEnum.offerEmailSent:
      return !!auditJson?.['Viewed by buyer']
    case MicrosoftOfferStageToStatusRelationMapEnum.offerViewed:
      return !!auditJson?.['Pending purchase']
    case MicrosoftOfferStageToStatusRelationMapEnum.offerTermsAcceptedBuyer:
      return hidePendingAccountConfiguration
        ? !!auditJson?.Accepted
        : !!auditJson?.['Pending account configuration']
    case MicrosoftOfferStageToStatusRelationMapEnum.offerPurchaseComplete:
      return !!auditJson?.Accepted
    default:
      return false
  }
}
